import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { BookOpen, Filter } from 'lucide-react';
import EmbeddedModule from './EmbeddedModule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGULATORS = ['ALL', 'RBI', 'SEBI', 'FIU-IND', 'IRDAI', 'NHB', 'PFRDA', 'MCA', 'I4C / MHA', 'CCI', 'MeitY'];
const IMPACT_COLORS = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-amber-100 text-amber-800 border-amber-200',
  medium: 'bg-blue-100 text-blue-700 border-blue-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200'
};

const RuleCockpit = () => {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [reg, setReg] = useState('ALL');
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/rules`).then((r) => setRules(r.data)).catch(() => setRules([]));
    axios.get(`${API}/rules/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const filtered = useMemo(() => {
    return rules
      .filter((r) => (reg === 'ALL' ? true : r.regulator === reg))
      .filter((r) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return [r.title, r.summary, r.circular_no].join(' ').toLowerCase().includes(q);
      });
  }, [rules, reg, search]);

  return (
    <EmbeddedModule
      eyebrow="Rules Engine · Big-10 Regulators"
      title="Rule Cockpit"
      tagline="Indian banking regulation in one screen — RBI, SEBI, FIU-IND, IRDAI, NHB, PFRDA, MCA, I4C, CCI, MeitY. Last-month top updates marked ready-to-deploy."
      badgeText="Last-month Top 5 Loaded"
      accentClass="from-amber-600 via-orange-600 to-rose-600"
      embedUrl="https://rulecockpit.aisentinelgsuite.com"
      embedTitle="rulecockpit.aisentinelgsuite.com"
      testId="rule-cockpit-page"
      kpis={[
        { label: 'Tracked Rules', value: stats?.total ?? '—' },
        { label: 'High / Critical', value: stats?.high_impact ?? '—' },
        { label: 'Ready Templates', value: stats?.ready_templates ?? '—' },
        { label: 'Regulators', value: stats ? Object.keys(stats.by_regulator).length : '—' }
      ]}
    >
      <Card data-testid="rule-cockpit-list">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center space-x-2">
              <BookOpen className="w-5 h-5 text-amber-600" />
              <span>Live Rule Feed</span>
              <Badge variant="secondary">{filtered.length}</Badge>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search rules..."
                className="h-9 w-56"
                data-testid="rules-search"
              />
              <div className="flex items-center gap-1 flex-wrap">
                {REGULATORS.map((r) => (
                  <button
                    key={r}
                    data-testid={`rule-filter-${r}`}
                    onClick={() => setReg(r)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                      reg === r
                        ? 'bg-amber-600 text-white border-amber-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {filtered.map((rule) => (
              <div
                key={rule.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-amber-300 hover:shadow-sm transition-all bg-white"
                data-testid={`rule-${rule.id}`}
              >
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="outline" className="text-[10px]">{rule.regulator}</Badge>
                      <Badge className={`text-[10px] border ${IMPACT_COLORS[rule.impact] || ''}`}>
                        {rule.impact}
                      </Badge>
                      {rule.ready_template && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                          Ready Template
                        </Badge>
                      )}
                      <span className="text-[11px] text-gray-500 font-mono">{rule.circular_no}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{rule.title}</p>
                    <p className="text-xs text-gray-600 mt-1">{rule.summary}</p>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-500">
                      <span>Issued: <span className="font-medium text-gray-700">{rule.issued_on || '—'}</span></span>
                      <span>Effective: <span className="font-medium text-gray-700">{rule.effective_from || '—'}</span></span>
                      {rule.applicable_to?.length > 0 && (
                        <span className="truncate">Applies: {rule.applicable_to.join(', ')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">No rules match the current filter.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </EmbeddedModule>
  );
};

export default RuleCockpit;
